// src/pages/Dashboard/DashboardPage.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { request } from "../../services/api";
import { GiGreenhouse } from "react-icons/gi";
import { LuHardDriveDownload, LuHardDriveUpload } from "react-icons/lu";
import { FaThermometerHalf, FaTint, FaWind, FaRedo } from "react-icons/fa";
import styles from "./Dashboard.module.css";
import UserMenu from "../../components/UserMenu/UserMenu";
import { CircuitBackgroundLayer } from "../../components/CircuitBackground/AnimatedCircuitChipBackground";
import { useLiveSensors } from "../../hooks/useLiveSensors";
import { readOfflineCache, writeOfflineCache } from "../../utils/offlineCache";

// --- تنظیمات رنگ و متن وضعیت‌ها ---
const getStatusConfig = (status) => {
  switch (status) {
    case "online":
      return { color: "#52ae47", text: "آنلاین (زنده)", dotAnim: true };
    case "connecting":
      return { color: "#f59f00", text: "در حال اتصال...", dotAnim: true };
    case "offline":
      return { color: "#ff4d4d", text: "آفلاین (قطع)", dotAnim: false };
    case "suspended":
      return { color: "#718096", text: "توقف تلاش (قطع)", dotAnim: false };
    default:
      return { color: "#718096", text: "نامشخص", dotAnim: false };
  }
};

const getCustomName = (customNames, index, fallback = "") => {
  if (!customNames) return fallback;

  const name = customNames[index] ?? customNames[String(index)];

  if (name === undefined || name === null || String(name).trim() === "") {
    return fallback;
  }

  return String(name).trim();
};

// --- نرمال‌سازی مقدار اتصال ---
const isConnectedValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE" ||
    value === "on" ||
    value === "ON" ||
    value === "connected"
  );
};

// --- بررسی اینکه مقدار سنسور واقعاً قابل نمایش هست یا نه ---
const hasValidValue = (value) => {
  return (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    value !== "--" &&
    value !== "---" &&
    value !== "N/A" &&
    value !== "nan" &&
    value !== "NaN"
  );
};

// --- بررسی اتصال یا تعریف‌شدن یک سنسور/ورودی/خروجی ---
const isVisibleItem = (item) => {
  if (!item) return false;

  const connectValue =
    item.isConnect ?? item.isconnect ?? item.connected ?? item.is_connected;

  if (connectValue !== undefined && connectValue !== null) {
    return isConnectedValue(connectValue);
  }

  return hasValidValue(item.value);
};

// --- بررسی روشن/فعال بودن ورودی یا خروجی دیجیتال ---
const isActiveValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "on" ||
    value === "ON" ||
    value === "true" ||
    value === "TRUE"
  );
};

const getDigitalDisplayValue = (item) => {
  const value = item?.status ?? item?.value ?? "0";
  return isActiveValue(value) ? "1" : "0";
};

const normalizeLastStateType = (type) => {
  const normalized = String(type || "")
    .trim()
    .toLowerCase();

  if (normalized === "din" || normalized === "d_in" || normalized === "d-in") {
    return "dIn";
  }

  if (
    normalized === "dout" ||
    normalized === "d_out" ||
    normalized === "d-out"
  ) {
    return "dOut";
  }

  if (normalized === "humidity" || normalized === "humid") return "humi";

  return normalized;
};

const getFirstDisplayValue = (...values) => {
  for (const value of values) {
    if (hasValidValue(value)) return value;
  }

  return "--";
};

const formatLastSeenAt = (value) => {
  if (!value) return "نامشخص";

  try {
    const date = new Date(String(value).replace(" ", "T"));

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return date.toLocaleString("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const getLatestLastSeenAt = (rows) => {
  if (!Array.isArray(rows)) return "";

  let latestValue = "";
  let latestTimestamp = Number.NEGATIVE_INFINITY;

  rows.forEach((row) => {
    const value =
      row?.lastSeenAt || row?.last_seen_at || row?.lastSeen || "";

    if (!hasValidValue(value)) return;

    const timestamp = new Date(String(value).replace(" ", "T")).getTime();

    if (Number.isNaN(timestamp)) {
      if (!latestValue) latestValue = value;
      return;
    }

    if (timestamp > latestTimestamp) {
      latestTimestamp = timestamp;
      latestValue = value;
    }
  });

  return latestValue;
};

const buildLastStatePreview = (rows, customNames) => {
  const preview = {
    analogItems: [],
    dInItems: [],
    dOutItems: [],
  };

  if (!Array.isArray(rows)) return preview;

  rows.forEach((row) => {
    if (!row || row.isVisible === false) return;

    const type = normalizeLastStateType(
      row.type || row.sensor_type || row.sensorType,
    );
    const index = Number(row.index ?? row.sensor_index ?? 0);
    const lastSeenAt = row.lastSeenAt || row.last_seen_at || row.lastSeen || "";
    const lastSeenLabel = formatLastSeenAt(lastSeenAt);
    const rawValue = getFirstDisplayValue(
      row.value,
      row.lastNumericValue,
      row.last_numeric_value,
      row.valueText,
      row.lastValueText,
      row.last_value_text,
    );
    const unit = row.unit || row.lastUnit || row.last_unit || "";
    const setpoint = getFirstDisplayValue(
      row.setpoint,
      row.lastSetpoint,
      row.last_setpoint,
    );
    const customName = row.customName || row.custom_name || "";

    if (type === "temp") {
      preview.analogItems.push({
        key: `last-temp-${index}`,
        type: "temp",
        label:
          customName || getCustomName(customNames.temp, index, `دما ${index}`),
        value: rawValue,
        unit: unit || "°C",
        setpoint,
        triggered: isActiveValue(row.trig ?? row.lastTrig ?? row.last_trig),
        icon: <FaThermometerHalf />,
        isLastState: true,
        lastSeenAt,
        lastSeenLabel,
      });
      return;
    }

    if (type === "humi") {
      preview.analogItems.push({
        key: `last-humi-${index}`,
        type: "humid",
        label:
          customName ||
          getCustomName(customNames.humi, index, `رطوبت ${index}`),
        value: rawValue,
        unit: unit || "%",
        setpoint,
        triggered: isActiveValue(row.trig ?? row.lastTrig ?? row.last_trig),
        icon: <FaTint />,
        isLastState: true,
        lastSeenAt,
        lastSeenLabel,
      });
      return;
    }

    if (type === "wind") {
      preview.analogItems.push({
        key: "last-wind-0",
        type: "wind",
        label: customName || getCustomName(customNames.wind, 0, "باد"),
        value: rawValue,
        unit: unit || "km/h",
        setpoint,
        triggered: isActiveValue(row.trig ?? row.lastTrig ?? row.last_trig),
        icon: <FaWind />,
        isLastState: true,
        lastSeenAt,
        lastSeenLabel,
      });
      return;
    }

    if (type === "dIn" || type === "dOut") {
      const value = getFirstDisplayValue(rawValue, "0");
      const names = type === "dIn" ? customNames.dIn : customNames.dOut;
      const fallbackPrefix = type === "dIn" ? "ورودی" : "خروجی";

      const item = {
        item: {
          value: String(value),
          status: String(value),
          isConnect: false,
          connected: true,
          triggered: isActiveValue(row.trig ?? row.lastTrig ?? row.last_trig),
          isLastState: true,
          lastSeenAt,
          lastSeenLabel,
          settings: {
            enable: row.enable ?? row.lastEnable ?? row.last_enable ?? "",
          },
        },
        index,
        customName:
          customName ||
          getCustomName(names, index, `${fallbackPrefix} ${index}`),
      };

      if (type === "dIn") {
        preview.dInItems.push(item);
      } else {
        preview.dOutItems.push(item);
      }
    }
  });

  preview.analogItems.sort((a, b) =>
    String(a.key).localeCompare(String(b.key)),
  );
  preview.dInItems.sort((a, b) => a.index - b.index);
  preview.dOutItems.sort((a, b) => a.index - b.index);

  return preview;
};

// --- محدودسازی آیتم‌ها برای اینکه کارت شلوغ نشود ---
const MAX_ANALOG_ITEMS_IN_CARD = 6;
const MAX_DIGITAL_ITEMS_IN_CARD = 16;

const createEmptyCustomNames = () => ({
  temp: {},
  humi: {},
  wind: {},
  dIn: {},
  dOut: {},
});

// --- کامپوننت کارت دستگاه ---
const DeviceCard = ({ device, onClick }) => {
  const { sensors, status, manualRetry } = useLiveSensors(device.device_serial);

  const [customNames, setCustomNames] = useState(() => {
    const cachedNames = readOfflineCache("dashboard-custom-names", device?.id);
    return cachedNames?.data || createEmptyCustomNames();
  });

  const [lastStateRows, setLastStateRows] = useState(() => {
    const cachedState = readOfflineCache("device-last-state", device?.id);
    return Array.isArray(cachedState?.data?.sensors)
      ? cachedState.data.sensors
      : [];
  });
  const [deviceMqttLastSeenAt, setDeviceMqttLastSeenAt] = useState(() => {
    const cachedState = readOfflineCache("device-last-state", device?.id);
    return cachedState?.data?.deviceMqttLastSeenAt || "";
  });
  const [keepLastStatePreview, setKeepLastStatePreview] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadCustomNames = async () => {
      if (!device?.id) return;

      try {
        const [tempNames, humiNames, windNames, dInNames, dOutNames] =
          await Promise.all([
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=temp`,
            ),
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=humi`,
            ),
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=wind`,
            ),
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=dIn`,
            ),
            request(
              `/manage_input_names.php?device_id=${device.id}&input_type=dOut`,
            ),
          ]);

        if (cancelled) return;

        const nextCustomNames = {
          temp: tempNames || {},
          humi: humiNames || {},
          wind: windNames || {},
          dIn: dInNames || {},
          dOut: dOutNames || {},
        };

        setCustomNames(nextCustomNames);
        writeOfflineCache(
          "dashboard-custom-names",
          device.id,
          nextCustomNames,
        );
      } catch (error) {
        console.error("Error loading dashboard custom names:", error);
      }
    };

    loadCustomNames();

    return () => {
      cancelled = true;
    };
  }, [device?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadLastState = async () => {
      if (!device?.id) {
        setLastStateRows([]);
        setDeviceMqttLastSeenAt("");
        return;
      }

      try {
        const data = await request(
          `/get_device_sensor_last_state.php?device_id=${device.id}`,
        );

        if (cancelled) return;

        const cachedLastSeenAt =
          readOfflineCache("device-last-state", device.id)?.data
            ?.deviceMqttLastSeenAt || "";

        if (Array.isArray(data)) {
          const nextLastSeenAt =
            getLatestLastSeenAt(data) || cachedLastSeenAt;

          setLastStateRows(data);
          setDeviceMqttLastSeenAt(nextLastSeenAt);
          writeOfflineCache("device-last-state", device.id, {
            sensors: data,
            deviceMqttLastSeenAt: nextLastSeenAt,
          });
        } else if (data?.status === "success" && Array.isArray(data.sensors)) {
          const nextLastSeenAt =
            data.deviceMqttLastSeenAt ||
            data.mqttLastSeenAt ||
            data.device_mqtt_last_seen_at ||
            getLatestLastSeenAt(data.sensors) ||
            cachedLastSeenAt;

          setLastStateRows(data.sensors);
          setDeviceMqttLastSeenAt(nextLastSeenAt);
          writeOfflineCache("device-last-state", device.id, {
            sensors: data.sensors,
            deviceMqttLastSeenAt: nextLastSeenAt,
          });
        } else {
          // Keep the previous cached preview instead of clearing the card during transient API/state refreshes.
          console.warn("Dashboard last state response was not usable:", data);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading dashboard last state:", error);
          // Do not clear lastStateRows here; offline preview should remain stable during retry/refresh.
        }
      }
    };

    loadLastState();

    return () => {
      cancelled = true;
    };
  }, [device?.id]);

  const statusConfig = getStatusConfig(status);
  const isSuspended = status === "suspended";
  const isOffline = status === "offline";

  const temps = Array.isArray(sensors?.temps) ? sensors.temps : [];
  const humidity = Array.isArray(sensors?.humidity) ? sensors.humidity : [];
  const dIn = Array.isArray(sensors?.dIn) ? sensors.dIn : [];
  const dOut = Array.isArray(sensors?.dOut) ? sensors.dOut : [];

  const connectedTemps = temps
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isVisibleItem(item));

  const connectedHumidity = humidity
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isVisibleItem(item));

  const windVisible = isVisibleItem(sensors?.wind);

  const connectedDIn = dIn
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isVisibleItem(item));

  const connectedDOut = dOut
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => isVisibleItem(item));

  const analogItems = [
    ...connectedTemps.map(({ item, index }) => ({
      key: `temp-${index}`,
      type: "temp",
      label: getCustomName(customNames.temp, index, `دما ${index}`),
      value: item.value,
      unit: "°C",
      setpoint: item.setpoint,
      triggered: item.triggered,
      icon: <FaThermometerHalf />,
    })),

    ...connectedHumidity.map(({ item, index }) => ({
      key: `humi-${index}`,
      type: "humid",
      label: getCustomName(customNames.humi, index, `رطوبت ${index}`),
      value: item.value,
      unit: "%",
      setpoint: item.setpoint,
      triggered: item.triggered,
      icon: <FaTint />,
    })),

    ...(windVisible
      ? [
          {
            key: "wind-0",
            type: "wind",
            label: getCustomName(customNames.wind, 0, "باد"),
            value: sensors.wind.value,
            unit: "km/h",
            setpoint: sensors.wind.setpoint,
            triggered: sensors.wind.triggered,
            icon: <FaWind />,
          },
        ]
      : []),
  ];

  const liveHasAnyVisibleData =
    analogItems.length > 0 ||
    connectedDIn.length > 0 ||
    connectedDOut.length > 0;

  const effectiveDeviceMqttLastSeenAt =
    deviceMqttLastSeenAt || getLatestLastSeenAt(lastStateRows);
  const deviceMqttLastSeenLabel = formatLastSeenAt(
    effectiveDeviceMqttLastSeenAt,
  );

  const lastStatePreview = buildLastStatePreview(lastStateRows, customNames);
  const hasLastStatePreview =
    lastStatePreview.analogItems.length > 0 ||
    lastStatePreview.dInItems.length > 0 ||
    lastStatePreview.dOutItems.length > 0;

  useEffect(() => {
    if (status === "online" && liveHasAnyVisibleData) {
      setKeepLastStatePreview(false);
      return;
    }

    if ((isOffline || isSuspended) && hasLastStatePreview) {
      setKeepLastStatePreview(true);
    }
  }, [
    status,
    liveHasAnyVisibleData,
    isOffline,
    isSuspended,
    hasLastStatePreview,
  ]);

  const shouldUseLastStatePreview =
    hasLastStatePreview &&
    !liveHasAnyVisibleData &&
    (isOffline || isSuspended || keepLastStatePreview);

  const shouldShowConnectionNotice =
    isOffline || isSuspended || keepLastStatePreview;

  const displayAnalogItems = shouldUseLastStatePreview
    ? lastStatePreview.analogItems
    : analogItems;
  const displayDIn = shouldUseLastStatePreview
    ? lastStatePreview.dInItems
    : connectedDIn;
  const displayDOut = shouldUseLastStatePreview
    ? lastStatePreview.dOutItems
    : connectedDOut;

  const visibleAnalogItems = displayAnalogItems.slice(
    0,
    MAX_ANALOG_ITEMS_IN_CARD,
  );
  const hiddenAnalogCount = Math.max(
    displayAnalogItems.length - MAX_ANALOG_ITEMS_IN_CARD,
    0,
  );

  const hasAnyVisibleData =
    visibleAnalogItems.length > 0 ||
    displayDIn.length > 0 ||
    displayDOut.length > 0;

  const handleRetryClick = (e) => {
    e.stopPropagation();
    if (manualRetry) manualRetry();
  };

  const handleDigitalDetailsClick = (e) => {
    e.stopPropagation();
    if (typeof onClick === "function") onClick();
  };

  const handleDigitalDetailsKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") return;

    e.preventDefault();
    handleDigitalDetailsClick(e);
  };

  const renderDigitalMiniGrid = (type) => {
    const isInput = type === "dIn";

    const items = (isInput ? displayDIn : displayDOut).slice(
      0,
      MAX_DIGITAL_ITEMS_IN_CARD,
    );
    const names = isInput ? customNames.dIn : customNames.dOut;
    const fallbackPrefix = isInput ? "ورودی" : "خروجی";

    return (
      <div className={styles.digitalMiniGrid}>
        {items.map(({ item, index, customName }) => {
          const value = getDigitalDisplayValue(item);
          const fullName =
            customName ||
            item?.customName ||
            getCustomName(names, index, `${fallbackPrefix} ${index}`);
          const lastSeenLabel = item?.lastSeenLabel;

          return (
            <div
              key={`${type}-mini-${index}`}
              className={`
                ${styles.digitalMiniCell}
                ${item?.isLastState ? styles.lastStateDigitalCell : ""}
              `}
              title={
                lastSeenLabel
                  ? `${fullName} - آخرین تغییر: ${lastSeenLabel}`
                  : fullName
              }
              data-tooltip={
                lastSeenLabel
                  ? `${fullName} - آخرین تغییر: ${lastSeenLabel}`
                  : fullName
              }
            >
              <span className={styles.digitalMiniIndex}>{index}</span>

              <span
                className={`
                ${styles.digitalMiniValue}
                ${value === "1" ? styles.digitalValueOn : styles.digitalValueOff}
              `}
              >
                {value}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.card} onClick={onClick}>
      <GiGreenhouse className={styles.cardIcon} />

      <div className={styles.cardContent}>
        <h3
          className={styles.deviceName}
          dir="rtl"
          style={{
            direction: "rtl",
            textAlign: "right",
            unicodeBidi: "plaintext",
            width: "100%",
          }}
        >
          {device.device_name}
        </h3>

        <div className={styles.deviceSerialText}>
          سریال: {device.device_serial}
        </div>

        <div
          className={`${styles.sensorSection} ${!hasAnyVisibleData ? styles.sensorSectionNoData : ""}`}
          style={{
            opacity: shouldUseLastStatePreview
              ? 1
              : isSuspended || isOffline
                ? 0.5
                : 1,
            filter: shouldUseLastStatePreview
              ? "none"
              : isSuspended
                ? "grayscale(100%)"
                : "none",
            transition: "all 0.3s ease",
          }}
        >
          {shouldShowConnectionNotice && (
            <div className={styles.lastStateNotice}>
              <div>
                ارتباط قطع است؛ آخرین مقادیر دریافت‌شده نمایش داده می‌شود و کنترل زنده در دسترس نیست.
              </div>

              {hasValidValue(effectiveDeviceMqttLastSeenAt) && (
                <div className={styles.lastStateDeviceSeenMeta}>
                  آخرین ارتباط دستگاه با سرور: {deviceMqttLastSeenLabel}
                </div>
              )}
            </div>
          )}

          {!hasAnyVisibleData ? (
            <div className={styles.noSensorState}>
              هنوز سنسور یا ورودی فعالی برای این دستگاه گزارش نشده است
            </div>
          ) : (
            <>
              {visibleAnalogItems.length > 0 && (
                <div className={styles.smartSensorGrid}>
                  {visibleAnalogItems.map((item) => (
                    <div
                      key={item.key}
                      className={`
                        ${styles.smartItem}
                        ${item.type === "temp" ? styles.tempItem : ""}
                        ${item.type === "humid" ? styles.humidItem : ""}
                        ${item.type === "wind" ? styles.windItem : ""}
                        ${item.isLastState ? styles.lastStateItem : ""}
                      `}
                    >
                      <div className={styles.smartIcon}>{item.icon}</div>

                      <div className={styles.smartText}>
                        <span className={styles.smartLabel}>{item.label}</span>

                        <span
                          className={styles.smartValue}
                          style={{
                            color: item.triggered ? "#ff4d4d" : "#fff",
                          }}
                        >
                          {item.value}
                          <small>{item.unit}</small>
                        </span>

                        {hasValidValue(item.setpoint) && (
                          <span className={styles.smartSet}>
                            حد: {item.setpoint}
                          </span>
                        )}

                        {item.isLastState && item.lastSeenLabel && (
                          <span className={styles.lastStateMeta}>
                            آخرین تغییر: {item.lastSeenLabel}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {hiddenAnalogCount > 0 && (
                <div className={styles.moreSensorsHint}>
                  + {hiddenAnalogCount} سنسور دیگر در صفحه مانیتور
                </div>
              )}

              {(displayDIn.length > 0 || displayDOut.length > 0) && (
                <div
                  className={styles.digitalSummaryBox}
                  onClick={(e) => e.stopPropagation()}
                >
                  {displayDIn.length > 0 && (
                    <div
                      className={`
                        ${styles.digitalSummaryRow}
                        ${styles.digitalInputRow}
                        ${styles.digitalAlwaysOpenRow}
                      `}
                    >
                      <div
                        className={styles.digitalSummaryHeader}
                        onClick={handleDigitalDetailsClick}
                        onKeyDown={handleDigitalDetailsKeyDown}
                        role="button"
                        tabIndex={0}
                        title="رفتن به صفحه مانیتورینگ"
                        aria-label="مشاهده ورودی‌های دیجیتال در صفحه مانیتورینگ"
                        style={{ cursor: "pointer" }}
                      >
                        <span className={styles.digitalSummaryTitleWithIcon}>
                          <LuHardDriveDownload
                            className={`
                              ${styles.digitalSummaryIcon}
                              ${styles.digitalInputIcon}
                            `}
                          />

                          <span>ورودی دیجیتال</span>
                        </span>
                      </div>

                      <div
                        className={styles.digitalDetailsCompactArea}
                        onClick={handleDigitalDetailsClick}
                        onKeyDown={handleDigitalDetailsKeyDown}
                        role="button"
                        tabIndex={0}
                        title="رفتن به صفحه مانیتورینگ"
                        aria-label="مشاهده ورودی‌های دیجیتال در صفحه مانیتورینگ"
                        style={{ cursor: "pointer" }}
                      >
                        {renderDigitalMiniGrid("dIn")}
                      </div>
                    </div>
                  )}

                  {displayDOut.length > 0 && (
                    <div
                      className={`
                        ${styles.digitalSummaryRow}
                        ${styles.digitalOutputRow}
                        ${styles.digitalAlwaysOpenRow}
                      `}
                    >
                      <div
                        className={styles.digitalSummaryHeader}
                        onClick={handleDigitalDetailsClick}
                        onKeyDown={handleDigitalDetailsKeyDown}
                        role="button"
                        tabIndex={0}
                        title="رفتن به صفحه مانیتورینگ"
                        aria-label="مشاهده خروجی‌های دیجیتال در صفحه مانیتورینگ"
                        style={{ cursor: "pointer" }}
                      >
                        <span className={styles.digitalSummaryTitleWithIcon}>
                          <LuHardDriveUpload
                            className={`
                              ${styles.digitalSummaryIcon}
                              ${styles.digitalOutputIcon}
                            `}
                          />

                          <span>خروجی دیجیتال</span>
                        </span>
                      </div>

                      <div
                        className={styles.digitalDetailsCompactArea}
                        onClick={handleDigitalDetailsClick}
                        onKeyDown={handleDigitalDetailsKeyDown}
                        role="button"
                        tabIndex={0}
                        title="رفتن به صفحه مانیتورینگ"
                        aria-label="مشاهده خروجی‌های دیجیتال در صفحه مانیتورینگ"
                        style={{ cursor: "pointer" }}
                      >
                        {renderDigitalMiniGrid("dOut")}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            marginTop: "15px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "28px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              className={statusConfig.dotAnim ? styles.statusDot : ""}
              style={{
                backgroundColor: statusConfig.color,
                boxShadow: statusConfig.dotAnim
                  ? `0 0 0 rgba(255,255,255,0.4)`
                  : "none",
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                display: "inline-block",
              }}
            ></span>

            <span
              style={{
                fontSize: "12px",
                color: statusConfig.color,
                fontWeight: "bold",
              }}
            >
              {statusConfig.text}
            </span>
          </div>

          {isSuspended && (
            <button
              onClick={handleRetryClick}
              className={styles.retryBtn}
              title="تلاش مجدد برای اتصال"
            >
              <FaRedo />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// --- تنظیمات retry داشبورد ---
const DASHBOARD_RETRY_BASE_MS = 1000;
const DASHBOARD_RETRY_MAX_MS = 30000;

const getDashboardRetryDelay = (attempt) => {
  const jitter = Math.floor(Math.random() * 700);
  return (
    Math.min(
      DASHBOARD_RETRY_MAX_MS,
      DASHBOARD_RETRY_BASE_MS * Math.pow(2, attempt),
    ) + jitter
  );
};

// --- کامپوننت اصلی صفحه ---
const DashboardPage = () => {
  const [devices, setDevices] = useState(() => {
    const cachedDevices = readOfflineCache("dashboard-devices");
    return Array.isArray(cachedDevices?.data) ? cachedDevices.data : [];
  });
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState("");

  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);
  const isMountedRef = useRef(false);

  const navigate = useNavigate();

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const loadDevices = useCallback(
    async (showLoading = false) => {
      if (!isMountedRef.current) return;

      if (showLoading) {
        setLoading(true);
      }

      try {
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          throw new Error("Browser is offline");
        }

        const data = await request("/get_devices.php");

        if (!isMountedRef.current) return;

        if (!data || data.status !== "success") {
          throw new Error(data?.message || "خطا در دریافت لیست دستگاه‌ها");
        }

        const nextDevices = data.devices || [];

        setDevices(nextDevices);
        writeOfflineCache("dashboard-devices", "", nextDevices);

        nextDevices.forEach((nextDevice) => {
          if (!nextDevice?.id) return;

          writeOfflineCache("device", nextDevice.id, nextDevice);

          if (nextDevice.permissions) {
            writeOfflineCache(
              "device-permissions",
              nextDevice.id,
              nextDevice.permissions,
            );
          }
        });
        setConnectionError("");
        retryAttemptRef.current = 0;
        clearRetryTimer();
      } catch (error) {
        if (!isMountedRef.current) return;

        console.error("Error fetching devices:", error);

        const cachedDevices = readOfflineCache("dashboard-devices");
        const hasCachedDevices =
          Array.isArray(cachedDevices?.data) && cachedDevices.data.length > 0;
        const cacheMessage = hasCachedDevices
          ? " آخرین اطلاعات ذخیره‌شده نمایش داده می‌شود."
          : "";

        setConnectionError(
          typeof navigator !== "undefined" && navigator.onLine === false
            ? `اینترنت قطع است.${cacheMessage} پس از اتصال، اطلاعات خودکار به‌روزرسانی می‌شود.`
            : `ارتباط با سرور برقرار نیست.${cacheMessage} تلاش مجدد خودکار انجام می‌شود...`,
        );

        clearRetryTimer();

        const delay = getDashboardRetryDelay(retryAttemptRef.current);

        retryTimerRef.current = setTimeout(() => {
          retryAttemptRef.current += 1;
          loadDevices(false);
        }, delay);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [clearRetryTimer],
  );

  useEffect(() => {
    isMountedRef.current = true;

    const handleOnline = () => {
      retryAttemptRef.current = 0;
      clearRetryTimer();
      setConnectionError("");
      loadDevices(devices.length === 0);
    };

    const handleOffline = () => {
      const cachedDevices = readOfflineCache("dashboard-devices");
      const hasCachedDevices =
        Array.isArray(cachedDevices?.data) && cachedDevices.data.length > 0;

      setConnectionError(
        hasCachedDevices
          ? "اینترنت قطع است. آخرین اطلاعات ذخیره‌شده نمایش داده می‌شود و پس از اتصال خودکار به‌روزرسانی خواهد شد."
          : "اینترنت قطع است. پس از اتصال، لیست دستگاه‌ها خودکار به‌روزرسانی می‌شود.",
      );
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    loadDevices(true);

    return () => {
      isMountedRef.current = false;

      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      clearRetryTimer();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDevices, clearRetryTimer]);

  const handleManualRefresh = () => {
    retryAttemptRef.current = 0;
    clearRetryTimer();
    loadDevices(devices.length === 0);
  };

  const handleCardClick = (device) => {
    if (!device?.id) return;

    const permissions = device.permissions || null;

    if (permissions) {
      sessionStorage.setItem(
        `device_permissions_${device.id}`,
        JSON.stringify(permissions),
      );
      writeOfflineCache("device-permissions", device.id, permissions);
    } else {
      sessionStorage.removeItem(`device_permissions_${device.id}`);
    }

    sessionStorage.setItem(`device_${device.id}`, JSON.stringify(device));
    writeOfflineCache("device", device.id, device);

    navigate(`/monitor/${device.id}`, {
      state: {
        device,
        permissions,
      },
    });
  };

  if (loading && devices.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        در حال دریافت لیست دستگاه‌ها...
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <CircuitBackgroundLayer />
      <div className={styles.overlay}></div>

      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title}>داشبورد گلخانه هوشمند</h2>
            <UserMenu dropdownAlign="left" />
          </div>
        </header>

        {connectionError && (
          <div
            style={{
              direction: "rtl",
              margin: "12px 0 16px 0",
              padding: "10px 12px",
              borderRadius: "12px",
              background: "rgba(245, 159, 0, 0.14)",
              border: "1px solid rgba(245, 159, 0, 0.35)",
              color: "#ffd43b",
              fontSize: "13px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <span>{connectionError}</span>

            <button
              type="button"
              onClick={handleManualRefresh}
              style={{
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
                borderRadius: "10px",
                padding: "6px 10px",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: "12px",
                fontWeight: "bold",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <FaRedo />
              تلاش مجدد
            </button>
          </div>
        )}

        <div className={styles.gridWrapper}>
          {devices.length === 0 ? (
            <div className={styles.emptyState}>
              <p>
                {connectionError
                  ? "در حال تلاش برای دریافت لیست دستگاه‌ها..."
                  : "هیچ دستگاهی یافت نشد."}
              </p>
              <small>
                {connectionError
                  ? "نیازی به refresh صفحه نیست؛ اتصال به‌صورت خودکار بررسی می‌شود."
                  : "لطفاً اتصال دیتابیس را بررسی کنید."}
              </small>
            </div>
          ) : (
            <div className={styles.grid}>
              {devices.map((device) => (
                <DeviceCard
                  key={device.id}
                  device={device}
                  onClick={() => handleCardClick(device)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
